/**
 * Syntax Visualization Compare Page
 * Compare arc-style vs bracket-style vs tree-style dependency graph rendering
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import DependencyGraph from '../../components/dependency-graph/DependencyGraph';
import type { DependencyGraphData, WordNode, DependencyArc } from '../../components/dependency-graph/types';

// ── Sample data ──
const SAMPLE_DATA: DependencyGraphData = {
  sentence: 'Researchers at Rothamsted Research in Hertfordshire have developed gene-edited wheat that produces bread with substantially reduced levels of acrylamide, a probable carcinogen.',
  words: [
    { id: 'w1', word: 'Researchers', role: 'Subject', roleZh: '主詞', color: '#22c55e' },
    { id: 'w2', word: 'at', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w3', word: 'Rothamsted', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w4', word: 'Research', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w5', word: 'in', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w6', word: 'Hertfordshire', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w7', word: 'have', role: 'Verb', roleZh: '動詞', color: '#3b82f6' },
    { id: 'w8', word: 'developed', role: 'Verb', roleZh: '動詞', color: '#3b82f6' },
    { id: 'w9', word: 'gene-edited', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w10', word: 'wheat', role: 'Object', roleZh: '受詞', color: '#f59e0b' },
    { id: 'w11', word: 'that', role: 'Other', roleZh: '其他', color: '#6b7280' },
    { id: 'w12', word: 'produces', role: 'Verb', roleZh: '動詞', color: '#3b82f6' },
    { id: 'w13', word: 'bread', role: 'Object', roleZh: '受詞', color: '#f59e0b' },
    { id: 'w14', word: 'with', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w15', word: 'substantially', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w16', word: 'reduced', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w17', word: 'levels', role: 'Object', roleZh: '受詞', color: '#f59e0b' },
    { id: 'w18', word: 'of', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 'w19', word: 'acrylamide', role: 'Object', roleZh: '受詞', color: '#f59e0b' },
  ],
  arcs: [
    { from: 'w1', to: 'w8', label: '主詞', labelEn: 'nsubj' },
    { from: 'w2', to: 'w1', label: '介系詞修飾', labelEn: 'prep' },
    { from: 'w3', to: 'w2', label: '介系詞受詞', labelEn: 'pobj' },
    { from: 'w4', to: 'w3', label: '名詞修飾', labelEn: 'compound' },
    { from: 'w5', to: 'w4', label: '介系詞修飾', labelEn: 'prep' },
    { from: 'w6', to: 'w5', label: '介系詞受詞', labelEn: 'pobj' },
    { from: 'w7', to: 'w8', label: '助動詞', labelEn: 'aux' },
    { from: 'w9', to: 'w10', label: '形容詞修飾', labelEn: 'amod' },
    { from: 'w10', to: 'w8', label: '直接受詞', labelEn: 'dobj' },
    { from: 'w11', to: 'w12', label: '關係代名詞', labelEn: 'nsubj' },
    { from: 'w12', to: 'w10', label: '關係子句', labelEn: 'relcl' },
    { from: 'w13', to: 'w12', label: '直接受詞', labelEn: 'dobj' },
    { from: 'w14', to: 'w13', label: '介系詞修飾', labelEn: 'prep' },
    { from: 'w15', to: 'w16', label: '副詞修飾', labelEn: 'advmod' },
    { from: 'w16', to: 'w17', label: '形容詞修飾', labelEn: 'amod' },
    { from: 'w17', to: 'w14', label: '介系詞受詞', labelEn: 'pobj' },
    { from: 'w18', to: 'w17', label: '介系詞修飾', labelEn: 'prep' },
    { from: 'w19', to: 'w18', label: '介系詞受詞', labelEn: 'pobj' },
  ],
  animationSteps: [
    { nodeIds: ['w1'], explanation: '主詞：Researchers（研究人員）是句子的主角' },
    { nodeIds: ['w1', 'w2', 'w3', 'w4', 'w5', 'w6'], explanation: '主詞 + 修飾：at Rothamsted Research in Hertfordshire 說明研究人員的所屬單位' },
    { nodeIds: ['w7', 'w8'], explanation: '動詞：have developed（已開發出）表示完成式' },
    { nodeIds: ['w9', 'w10'], explanation: '受詞：gene-edited wheat（基因編輯小麥）' },
    { nodeIds: ['w10', 'w11', 'w12', 'w13'], explanation: '關係子句：that produces bread（能製造出麵包）修飾 wheat' },
    { nodeIds: ['w13', 'w14', 'w15', 'w16', 'w17', 'w18', 'w19'], explanation: '介系詞片語：with substantially reduced levels of acrylamide（含有大幅降低的丙烯醯胺含量）' },
  ],
};

const SHORT_SAMPLE: DependencyGraphData = {
  sentence: 'The cat sat on the mat.',
  words: [
    { id: 's1', word: 'The', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 's2', word: 'cat', role: 'Subject', roleZh: '主詞', color: '#22c55e' },
    { id: 's3', word: 'sat', role: 'Verb', roleZh: '動詞', color: '#3b82f6' },
    { id: 's4', word: 'on', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 's5', word: 'the', role: 'Modifier', roleZh: '修飾語', color: '#a855f7' },
    { id: 's6', word: 'mat', role: 'Object', roleZh: '受詞', color: '#f59e0b' },
  ],
  arcs: [
    { from: 's1', to: 's2', label: '冠詞', labelEn: 'det' },
    { from: 's2', to: 's3', label: '主詞', labelEn: 'nsubj' },
    { from: 's4', to: 's3', label: '介系詞修飾', labelEn: 'prep' },
    { from: 's5', to: 's6', label: '冠詞', labelEn: 'det' },
    { from: 's6', to: 's4', label: '介系詞受詞', labelEn: 'pobj' },
  ],
  animationSteps: [
    { nodeIds: ['s1', 's2'], explanation: '主詞：The cat（那隻貓）' },
    { nodeIds: ['s3'], explanation: '動詞：sat（坐）' },
    { nodeIds: ['s4', 's5', 's6'], explanation: '介系詞片語：on the mat（在墊子上）' },
  ],
};

// ════════════════════════════════════════
// A. Arc View (original curved arcs above words)
// ════════════════════════════════════════
const estimateWordWidth = (word: string): number => {
  let width = 0;
  for (const ch of word) {
    if (/[mwMW]/.test(ch)) width += 12;
    else if (/[iltfjr!|.,;:'()]/.test(ch)) width += 5;
    else if (/[A-Z]/.test(ch)) width += 10;
    else width += 8;
  }
  return Math.max(width, 30);
};

/** Estimate label width in SVG units (rough: ~7px per CJK char, ~6px per latin char at font-size 11) */
const estimateLabelWidth = (label: string): number => {
  let w = 0;
  for (const ch of label) {
    w += ch.charCodeAt(0) > 0x2e80 ? 11 : 6.5;
  }
  return w;
};

/** Arc arcs layer with label collision detection */
const ArcArcsLayer: React.FC<{
  arcs: DependencyArc[];
  words: WordNode[];
  positions: number[];
  arcBaseY: number;
  isHL: (id: string) => boolean;
  getColor: (id: string) => string;
}> = ({ arcs, words, positions, arcBaseY, isHL, getColor }) => {
  // Pre-compute arc geometry + label positions, then resolve collisions
  const arcData = useMemo(() => {
    return arcs.map((arc, i) => {
      const fi = words.findIndex(w => w.id === arc.from);
      const ti = words.findIndex(w => w.id === arc.to);
      if (fi === -1 || ti === -1) return null;
      const fromX = positions[fi];
      const toX = positions[ti];
      const dist = Math.abs(ti - fi);
      const arcH = Math.min(30 + dist * 20, 110);
      const controlY = arcBaseY - arcH;
      const midX = (fromX + toX) / 2;
      const curvePeakY = (arcBaseY + controlY) / 2;
      const path = `M ${fromX} ${arcBaseY} Q ${midX} ${controlY} ${toX} ${arcBaseY}`;
      return { arc, index: i, fromX, toX, controlY, midX, curvePeakY, path, labelW: estimateLabelWidth(arc.label) };
    }).filter(Boolean) as Array<{
      arc: DependencyArc; index: number; fromX: number; toX: number;
      controlY: number; midX: number; curvePeakY: number; path: string; labelW: number;
    }>;
  }, [arcs, words, positions, arcBaseY]);

  // Resolve label collisions: only for visible (highlighted) labels
  const labelPositions = useMemo(() => {
    const LABEL_H = 14; // approximate label height
    const PAD_X = 6;    // horizontal padding between labels
    const PAD_Y = 2;    // vertical padding between labels

    // Collect labels that will be shown
    const visibleLabels = arcData
      .filter(d => isHL(d.arc.from) && isHL(d.arc.to))
      .map(d => ({
        index: d.index,
        x: d.midX,
        y: d.curvePeakY - 5,
        w: d.labelW,
        h: LABEL_H,
      }));

    // Sort by x position for sweep-line collision resolution
    visibleLabels.sort((a, b) => a.x - b.x);

    // Greedily push overlapping labels upward
    for (let i = 0; i < visibleLabels.length; i++) {
      for (let j = i + 1; j < visibleLabels.length; j++) {
        const a = visibleLabels[i];
        const b = visibleLabels[j];
        // Check horizontal overlap
        const xOverlap = Math.abs(a.x - b.x) < (a.w + b.w) / 2 + PAD_X;
        if (!xOverlap) continue;
        // Check vertical overlap
        const yOverlap = Math.abs(a.y - b.y) < LABEL_H + PAD_Y;
        if (yOverlap) {
          // Push the one with higher y (lower on screen) upward
          if (b.y >= a.y) {
            b.y = a.y - LABEL_H - PAD_Y;
          } else {
            a.y = b.y - LABEL_H - PAD_Y;
          }
        }
      }
    }

    const map = new Map<number, { x: number; y: number }>();
    visibleLabels.forEach(l => map.set(l.index, { x: l.x, y: l.y }));
    return map;
  }, [arcData, isHL]);

  return (
    <g>
      {arcData.map(d => {
        const fromHL = isHL(d.arc.from);
        const toHL = isHL(d.arc.to);
        const arcHL = fromHL || toHL;
        const showLabel = fromHL && toHL;
        const color = getColor(d.arc.to);
        const labelPos = labelPositions.get(d.index);
        return (
          <g key={d.index}>
            <path d={d.path} fill="none" stroke={arcHL ? color : '#ddd'} strokeWidth={arcHL ? 2.5 : 1.5}
              markerEnd="url(#arc-arrow)" style={{ transition: 'all 0.25s' }} />
            {showLabel && labelPos && (
              <text x={labelPos.x} y={labelPos.y} textAnchor="middle" fontSize="11" fontWeight="600" fill={color}>
                {d.arc.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

const ArcView: React.FC<{
  data: DependencyGraphData;
  highlightedNodes: Set<string>;
}> = ({ data, highlightedNodes }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { words, arcs } = data;
  const nodeColors = useMemo(() => {
    const c: Record<string, string> = {};
    words.forEach(w => { c[w.id] = w.color; });
    return c;
  }, [words]);

  const { positions, svgWidth } = useMemo(() => {
    const GAP = 16;
    const PADDING = 40;
    const wordWidths = words.map(w => estimateWordWidth(w.word));
    const naturalWidth = wordWidths.reduce((s, w) => s + w, 0) + GAP * (words.length - 1) + PADDING * 2;
    const availableWidth = Math.max(containerWidth - 32, 400);
    const useWidth = Math.max(naturalWidth, availableWidth);
    const scale = useWidth / naturalWidth;
    const pos: number[] = [];
    let x = PADDING;
    words.forEach((_, i) => {
      const w = wordWidths[i] * scale;
      pos.push(x + w / 2);
      x += w + GAP * scale;
    });
    return { positions: pos, svgWidth: useWidth };
  }, [words, containerWidth]);

  const { svgHeight, wordY, arcBaseY } = useMemo(() => {
    let maxH = 30;
    arcs.forEach(arc => {
      const fi = words.findIndex(w => w.id === arc.from);
      const ti = words.findIndex(w => w.id === arc.to);
      if (fi === -1 || ti === -1) return;
      const h = Math.min(30 + Math.abs(ti - fi) * 20, 110);
      if (h > maxH) maxH = h;
    });
    const baseY = 12 + maxH + 20;
    return { svgHeight: baseY + 30, wordY: baseY + 20, arcBaseY: baseY - 5 };
  }, [words, arcs]);

  const getColor = (id: string) => nodeColors[id] || '#666';
  const isHL = (id: string) => highlightedNodes.size === 0 || highlightedNodes.has(id);

  return (
    <div ref={containerRef} style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        <defs>
          <marker id="arc-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth">
            <path d="M 0 0 L 6 4 L 0 8" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </marker>
        </defs>
        <ArcArcsLayer arcs={arcs} words={words} positions={positions} arcBaseY={arcBaseY} isHL={isHL} getColor={getColor} />
        <g>
          {words.map((word, i) => {
            const x = positions[i];
            const hl = isHL(word.id);
            return (
              <g key={word.id}>
                <circle cx={x} cy={arcBaseY} r={3} fill={hl ? getColor(word.id) : '#ccc'} />
                <text x={x} y={wordY} textAnchor="middle" fontSize={hl ? 17 : 16} fontWeight={hl ? 700 : 500}
                  fill={hl ? getColor(word.id) : '#333'} style={{ transition: 'all 0.25s' }}>
                  {word.word}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

// ════════════════════════════════════════
// C. Tree View (sentence-order with dependency arrows)
// Words appear in original sentence order; each word shows
// an arrow pointing to its head word with the relationship label.
// ════════════════════════════════════════
const TreeView: React.FC<{
  data: DependencyGraphData;
  highlightedNodes: Set<string>;
}> = ({ data, highlightedNodes }) => {
  // Build map: wordId → { headWord, label }
  const relations = useMemo(() => {
    const map = new Map<string, { headId: string; label: string }>();
    const headSet = new Set<string>();
    for (const arc of data.arcs) {
      // arc.from depends on arc.to
      map.set(arc.from, { headId: arc.to, label: arc.label });
      headSet.add(arc.to);
    }
    // Find root (word that has no head)
    const rootId = data.words.find(w => !map.has(w.id))?.id || null;
    return { map, rootId };
  }, [data.words, data.arcs]);

  const wordMap = useMemo(() => {
    const m = new Map<string, WordNode>();
    data.words.forEach(w => m.set(w.id, w));
    return m;
  }, [data.words]);

  return (
    <div className="tree-view">
      {data.words.map((word) => {
        const highlighted = highlightedNodes.size === 0 || highlightedNodes.has(word.id);
        const rel = relations.map.get(word.id);
        const headWord = rel ? wordMap.get(rel.headId) : null;
        const isRoot = word.id === relations.rootId;

        return (
          <div key={word.id} className="tree-row" style={{ opacity: highlighted ? 1 : 0.3, transition: 'opacity 0.25s' }}>
            <span className="tree-word" style={{ color: highlighted ? word.color : '#999', fontWeight: highlighted ? 700 : 400 }}>
              {word.word}
            </span>
            {isRoot ? (
              <span className="tree-root-badge">ROOT</span>
            ) : rel && headWord ? (
              <>
                <span className="tree-arrow-line">
                  <svg width="20" height="12" viewBox="0 0 20 12" style={{ verticalAlign: 'middle' }}>
                    <line x1="0" y1="6" x2="14" y2="6" stroke={highlighted ? word.color : '#ccc'} strokeWidth="1.5" />
                    <path d="M 12 2 L 18 6 L 12 10" fill="none" stroke={highlighted ? word.color : '#ccc'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="tree-label-badge" style={{ backgroundColor: `${word.color}20`, color: word.color }}>
                  {rel.label}
                </span>
                <span className="tree-arrow-line">
                  <svg width="20" height="12" viewBox="0 0 20 12" style={{ verticalAlign: 'middle' }}>
                    <line x1="0" y1="6" x2="14" y2="6" stroke={highlighted ? headWord.color : '#ccc'} strokeWidth="1.5" />
                    <path d="M 12 2 L 18 6 L 12 10" fill="none" stroke={highlighted ? headWord.color : '#ccc'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="tree-head" style={{ color: highlighted ? headWord.color : '#bbb' }}>
                  {headWord.word}
                </span>
              </>
            ) : null}
            <span className="tree-role">{word.roleZh}</span>
          </div>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════
// Shared step controls
// ════════════════════════════════════════
const StepControls: React.FC<{
  step: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  explanation?: string;
}> = ({ step, total, onPrev, onNext, explanation }) => (
  <div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
      <button onClick={onPrev} disabled={step < 0} className="sc-btn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
      </button>
      <span style={{ fontSize: 13, color: '#64748b', minWidth: 80, textAlign: 'center' }}>
        {step >= 0 ? `${step + 1} / ${total}` : '點擊箭頭開始'}
      </span>
      <button onClick={onNext} disabled={step >= total - 1} className="sc-btn">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
      </button>
    </div>
    {explanation && (
      <div style={{ padding: '8px 12px', background: '#fffbeb', borderRadius: 8, borderLeft: '3px solid #f59e0b', marginBottom: 12, fontSize: 14, color: '#92400e', textAlign: 'center' }}>
        {explanation}
      </div>
    )}
  </div>
);

// ════════════════════════════════════════
// Main Page
// ════════════════════════════════════════
const SyntaxComparePage: React.FC = () => {
  const [selectedSample, setSelectedSample] = useState<'long' | 'short'>('long');
  const [arcStep, setArcStep] = useState(-1);
  const [treeStep, setTreeStep] = useState(-1);
  const sampleData = selectedSample === 'long' ? SAMPLE_DATA : SHORT_SAMPLE;

  const makeHL = (step: number) => {
    if (step < 0 || step >= sampleData.animationSteps.length) return new Set<string>();
    return new Set(sampleData.animationSteps[step].nodeIds);
  };

  const arcHL = useMemo(() => makeHL(arcStep), [arcStep, sampleData]);
  const treeHL = useMemo(() => makeHL(treeStep), [treeStep, sampleData]);

  const resetSteps = () => { setArcStep(-1); setTreeStep(-1); };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>句法結構視覺化比較</h1>
      <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
        比較三種呈現方式：Arc（弧線）、Bracket（水平連線）、Tree（依存關係列表）
      </p>

      {/* Sample selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {(['short', 'long'] as const).map(key => (
          <button key={key} onClick={() => { setSelectedSample(key); resetSteps(); }}
            style={{
              padding: '8px 16px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
              border: selectedSample === key ? '2px solid #667eea' : '1px solid #ddd',
              background: selectedSample === key ? '#eef2ff' : 'white',
              fontWeight: selectedSample === key ? 600 : 400,
            }}>
            {key === 'short' ? 'Short: The cat sat on the mat.' : 'Long: Researchers at Rothamsted...'}
          </button>
        ))}
      </div>

      {/* Sentence */}
      <div style={{ padding: '12px 20px', background: '#fffbeb', borderRadius: 10, borderLeft: '3px solid #f59e0b', marginBottom: 24, fontSize: 16, lineHeight: 1.6, color: '#333' }}>
        {sampleData.sentence}
      </div>

      {/* Three views stacked */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

        {/* A. Arc (original) */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#333' }}>
            A. Arc（弧線） <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}>— 原始設計，弧線高度與字距成正比</span>
          </h2>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 }}>
            <StepControls
              step={arcStep} total={sampleData.animationSteps.length}
              onPrev={() => setArcStep(s => Math.max(-1, s - 1))}
              onNext={() => setArcStep(s => Math.min(sampleData.animationSteps.length - 1, s < 0 ? 0 : s + 1))}
              explanation={arcStep >= 0 ? sampleData.animationSteps[arcStep]?.explanation : undefined}
            />
            <ArcView data={sampleData} highlightedNodes={arcHL} />
          </div>
        </div>

        {/* B. Bracket (new) */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#333' }}>
            B. Bracket（水平連線） <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}>— 垂直下拉 + 水平連線，高度只跟嵌套層數有關</span>
          </h2>
          <div className="bracket-compare-wrapper" style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <DependencyGraph data={sampleData} showControls={true} showLegend={false} autoPlay={false} />
          </div>
        </div>

        {/* B-mobile. Bracket mobile preview */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#333' }}>
            B-mobile. Bracket 手機版 <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}>— 375px 寬度模擬</span>
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="bracket-compare-wrapper" style={{
              width: 375, maxWidth: '100%',
              background: 'white', borderRadius: 12, border: '2px dashed #cbd5e1', overflow: 'hidden',
            }}>
              <DependencyGraph data={sampleData} showControls={true} showLegend={false} autoPlay={false} />
            </div>
          </div>
        </div>

        {/* C. Tree */}
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12, color: '#333' }}>
            C. Tree（依存關係列表） <span style={{ fontSize: 13, fontWeight: 400, color: '#999' }}>— 按原句順序排列，箭頭指向所依存的詞</span>
          </h2>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 }}>
            <StepControls
              step={treeStep} total={sampleData.animationSteps.length}
              onPrev={() => setTreeStep(s => Math.max(-1, s - 1))}
              onNext={() => setTreeStep(s => Math.min(sampleData.animationSteps.length - 1, s < 0 ? 0 : s + 1))}
              explanation={treeStep >= 0 ? sampleData.animationSteps[treeStep]?.explanation : undefined}
            />
            <TreeView data={sampleData} highlightedNodes={treeHL} />
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 24, padding: '12px', background: '#f8fafc', borderRadius: 8 }}>
        {[
          { color: '#22c55e', label: '主詞 (Subject)' },
          { color: '#a855f7', label: '修飾語 (Modifier)' },
          { color: '#3b82f6', label: '動詞 (Verb)' },
          { color: '#f59e0b', label: '受詞 (Object)' },
          { color: '#6b7280', label: '其他 (Other)' },
        ].map(item => (
          <div key={item.color} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
            {item.label}
          </div>
        ))}
      </div>

      <style>{`
        .bracket-compare-wrapper .dependency-graph-container {
          max-width: none;
        }
        .sc-btn {
          width: 32px; height: 32px; border-radius: 50%;
          border: 1px solid #e2e8f0; background: white;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.15s; color: #475569;
        }
        .sc-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #667eea; color: #667eea; }
        .sc-btn:disabled { opacity: 0.3; cursor: not-allowed; }
        .tree-view {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
        }
        .tree-row {
          display: flex; align-items: center; gap: 6px; white-space: nowrap;
          padding: 5px 8px; border-radius: 6px; transition: background 0.15s;
        }
        .tree-row:hover { background: #f8fafc; }
        .tree-label-badge {
          font-size: 11px; font-weight: 500; padding: 1px 6px; border-radius: 4px; flex-shrink: 0;
        }
        .tree-word {
          font-size: 15px; min-width: 60px; flex-shrink: 0;
        }
        .tree-head {
          font-size: 14px; font-weight: 500;
        }
        .tree-arrow-line {
          flex-shrink: 0; display: inline-flex; align-items: center;
        }
        .tree-root-badge {
          font-size: 10px; font-weight: 700; color: #64748b; background: #f1f5f9;
          padding: 1px 8px; border-radius: 4px; letter-spacing: 0.5px;
        }
        .tree-role {
          font-size: 11px; color: #94a3b8; margin-left: auto;
        }
      `}</style>
    </div>
  );
};

export default SyntaxComparePage;
